/**
 * Context Compaction Utility
 *
 * Compresses page context data into a token-efficient format for LLM injection.
 * Uses pipe-delimited key=value pairs with abbreviated keys.
 *
 * Research basis:
 * - Pipe-delimited format is ~50-55% more token-efficient than natural language
 * - LLMs natively comprehend financial abbreviations (px, pe, mktcap, div)
 * - Structured formats improve LLM data extraction vs prose
 *
 * Example output:
 *   Viewing AAPL on Stock Research.
 *   symbol:AAPL|px:$182.47|pe:28.6|pb:45.2|mktcap:2.8T|sector:Technology|val:Overvalued|health:Strong|div:0.55%|rating:Buy|chg:+1.23%
 *   Use this context to answer questions.
 */

import type { PageContext } from '../contexts/FloatingChatContext';

// Hard caps so a single oversized upstream field can't blow up every chat
// request. Backend `services/page_context_summarizer` further distills via
// FAST_LITE_MODEL when this raw blob is still oversized after compaction.
const MAX_TOTAL_CHARS = 1200;       // ~300 tokens — generous for a context slug
const MAX_LIST_ITEMS = 5;            // top-N truncation for comma-joined fields
const MAX_FIELD_CHARS = 240;         // per-field clip for any single value

/** Truncate a string field, append "…+N" sentinel when clipped. */
const clipField = (s: string, label: string): string => {
  if (s.length <= MAX_FIELD_CHARS) return s;
  const overflow = s.length - MAX_FIELD_CHARS;
  return `${s.slice(0, MAX_FIELD_CHARS)}… (+${overflow} more chars of ${label})`;
};

/** Truncate a comma/semicolon-joined list field to MAX_LIST_ITEMS. */
const clipList = (s: string, sep = ','): string => {
  const items = s.split(sep).map(x => x.trim()).filter(Boolean);
  if (items.length <= MAX_LIST_ITEMS) return s;
  return `${items.slice(0, MAX_LIST_ITEMS).join(sep + ' ')}${sep} (+${items.length - MAX_LIST_ITEMS} more)`;
};

/** Format large numbers with scale suffix */
const fmtNum = (n: number): string => {
  const abs = Math.abs(n);
  if (abs >= 1e12) return `${(n / 1e12).toFixed(1)}T`;
  if (abs >= 1e9) return `${(n / 1e9).toFixed(1)}B`;
  if (abs >= 1e6) return `${(n / 1e6).toFixed(1)}M`;
  if (abs >= 1e3) return `${(n / 1e3).toFixed(1)}K`;
  return n.toFixed(0);
};

/** Format change percent with sign */
const fmtChg = (v: number): string =>
  `${v >= 0 ? '+' : ''}${v.toFixed(2)}%`;

/**
 * Compact page context into a token-efficient string for LLM injection.
 *
 * ~42 tokens for stock context (vs ~90 for natural language prose).
 */
export const compactPageContext = (ctx: PageContext): string => {
  const d = ctx.relevantData || {};
  const pairs: string[] = [];

  // Stock Research context
  if (d.symbol && d.companyName) {
    const header = `Viewing ${d.companyName} (${d.symbol}) on ${ctx.pageName}.`;

    pairs.push(`symbol:${d.symbol}`);
    if (d.price != null) pairs.push(`px:$${Number(d.price).toFixed(2)}`);
    if (d.peRatio != null) pairs.push(`pe:${Number(d.peRatio).toFixed(1)}`);
    if (d.pbRatio != null) pairs.push(`pb:${Number(d.pbRatio).toFixed(2)}`);
    if (d.marketCap != null) pairs.push(`mktcap:${fmtNum(Number(d.marketCap))}`);
    if (d.sector) pairs.push(`sector:${d.sector}`);
    if (d.valuationVerdict) pairs.push(`val:${d.valuationVerdict}`);
    if (d.healthVerdict) pairs.push(`health:${d.healthVerdict}`);
    // Already a percent: the only producer, StockResearch, passes
    // DividendModule's `dividendYield`, which that module normalises to a
    // percent (`DividendModule.ts`, `rawYield`). Multiplying again sent
    // Realty Income's 5.73 as "div:573.00%" ([[BUGS #303]]).
    if (d.dividendYield != null) pairs.push(`div:${Number(d.dividendYield).toFixed(2)}%`);
    if (d.dqiRating) pairs.push(`divqual:${d.dqiRating}`);
    if (d.consensusRating) pairs.push(`rating:${d.consensusRating}`);
    if (d.changePercent != null) pairs.push(`chg:${fmtChg(Number(d.changePercent))}`);

    return enforceTotalCap(`${header}\n${pairs.join('|')}\nUse this context to answer questions.`);
  }

  // Risk Results context
  if (d.riskScore != null && d.portfolioName) {
    const header = `Viewing risk results for "${d.portfolioName}" on ${ctx.pageName}.`;

    if (d.portfolioValue != null) pairs.push(`portval:$${fmtNum(Number(d.portfolioValue))}`);
    pairs.push(`risk:${d.riskScore}/100`);
    if (d.riskLevel) pairs.push(`level:${d.riskLevel}`);
    if (d.var95 != null) pairs.push(`var95:$${fmtNum(Number(d.var95))}`);
    if (d.sharpeRatio != null) pairs.push(`sharpe:${Number(d.sharpeRatio).toFixed(2)}`);
    if (d.beta != null) pairs.push(`beta:${Number(d.beta).toFixed(2)}`);
    if (d.topHoldings) pairs.push(`top:${clipList(String(d.topHoldings))}`);
    if (d.recommendations) pairs.push(`recs:${clipField(String(d.recommendations), 'recs')}`);

    return enforceTotalCap(`${header}\n${pairs.join('|')}\nUse this context to answer questions.`);
  }

  // Portfolio input context
  if (d.holdingsCount != null && d.pageType === 'risk-input') {
    const header = `User entering portfolio with ${d.holdingsCount} holdings on ${ctx.pageName}.`;
    if (d.symbols) pairs.push(`symbols:${clipList(String(d.symbols))}`);

    return enforceTotalCap(pairs.length > 0
      ? `${header}\n${pairs.join('|')}\nUse this context to answer questions.`
      : `${header}\nUse this context to answer questions.`);
  }

  // Watchlist context
  if (d.watchlistName) {
    const header = `Viewing watchlist "${d.watchlistName}" (${d.symbolCount || 0} symbols) on ${ctx.pageName}.`;
    if (d.symbols) pairs.push(`symbols:${clipList(String(d.symbols))}`);

    return enforceTotalCap(pairs.length > 0
      ? `${header}\n${pairs.join('|')}\nUse this context to answer questions.`
      : `${header}\nUse this context to answer questions.`);
  }

  // My Portfolios context
  if (d.portfolioCount != null) {
    const header = `User has ${d.portfolioCount} saved portfolios on ${ctx.pageName}.`;
    if (d.portfolioNames) pairs.push(`names:${clipList(String(d.portfolioNames))}`);

    return enforceTotalCap(pairs.length > 0
      ? `${header}\n${pairs.join('|')}\nUse this context to answer questions.`
      : `${header}\nUse this context to answer questions.`);
  }

  // Fallback: symbol only
  if (d.symbol) {
    return `Viewing ${d.symbol} on ${ctx.pageName}.\nUse this context to answer questions.`;
  }

  // Generic page context (no relevant data)
  return `User is on the "${ctx.pageName}" page.\nUse this context to answer questions.`;
};

/** Final hard cap on the whole string — last-line defence. The backend
 *  summarizer (FAST_LITE_MODEL) is the smart distiller; this just makes
 *  sure a runaway upstream payload can never blow up the request. */
const enforceTotalCap = (s: string): string => {
  if (s.length <= MAX_TOTAL_CHARS) return s;
  return `${s.slice(0, MAX_TOTAL_CHARS - 80)}\n[context truncated — ${s.length - MAX_TOTAL_CHARS + 80} more chars dropped]`;
};


/**
 * What the chat page's context chip says (ROADMAP "AI assistant — page
 * context", part (a)): the thing the reader was looking at, named the way the
 * page named it, never the internal slug. `null` when there is nothing worth
 * a chip, so the caller renders none.
 */
export const pageContextChipLabel = (ctx: PageContext | null | undefined): string | null => {
  if (!ctx || ctx.pageType === 'chat') return null;
  const d = ctx.relevantData || {};
  if (d.symbol) {
    return d.companyName ? `Using ${d.companyName} (${d.symbol})` : `Using ${d.symbol}`;
  }
  if (d.portfolioName) return `Using ${d.portfolioName}`;
  if (d.portfolioId) return `Using your ${ctx.pageName.toLowerCase()}`;
  return null;
};

/**
 * The `pageContext` a chat-page request carries: the remembered tool page,
 * compacted, exactly when the chip for it is shown — and nothing otherwise.
 * One decision in one place, so the chip and the request cannot disagree.
 */
export const pageContextForRequest = (ctx: PageContext | null | undefined): string | undefined =>
  pageContextChipLabel(ctx) ? compactPageContext(ctx as PageContext) : undefined;

/** Capitalised words a question uses that are not tickers. */
const NOT_A_TICKER = new Set([
  'I', 'A', 'AI', 'CEO', 'CFO', 'ETF', 'ETFS', 'IPO', 'EPS', 'USD', 'AUD', 'EUR', 'GBP', 'US', 'USA', 'UK', 'EU',
  'CPI', 'GDP', 'PPI', 'PMI', 'FED', 'SEC', 'ASX', 'NYSE', 'FY', 'Q1', 'Q2', 'Q3', 'Q4', 'TTM', 'YOY', 'DCF', 'ROE',
  'ROI', 'FCF', 'PE', 'OK', 'VS', 'IT', 'AND', 'OR', 'THE', 'FOR', 'NOT', 'IS', 'ARE', 'MY', 'ME', 'HOW', 'WHY',
  'WHAT', 'DOES', 'DO', 'IF', 'TO', 'IN', 'ON', 'OF', 'AT', 'BY', 'BUY', 'SELL', 'HOLD',
]);

/**
 * Whether the reader names a different stock than the page the chat was
 * opened from ([[BUGS #537]]): the chip read "Using Microsoft Corporation
 * (MSFT)" through a whole conversation about GOOG, and the page travelled
 * with every message. A ticker the reader WROTE — capitals or a $cashtag,
 * as the backend reads it — that is not the page's symbol.
 */
export const namesAnotherStock = (question: string, ctx: PageContext | null | undefined): boolean => {
  const page = String(ctx?.relevantData?.symbol || '').toUpperCase();
  if (!page) return false;
  const tokens = (question || '').match(/\$?[A-Za-z]{1,5}(?:\.[A-Za-z]{1,2})?\b/g) || [];
  return tokens.some((raw) => {
    const cashtag = raw.startsWith('$');
    const word = raw.replace(/^\$/, '');
    // Written in capitals, or a cashtag in any case; never a single letter or an ordinary word.
    if (!cashtag && (word !== word.toUpperCase() || word.length < 2)) return false;
    const sym = word.toUpperCase();
    return !NOT_A_TICKER.has(sym) && sym !== page && sym.split('.')[0] !== page.split('.')[0];
  });
};
